import type { FileDetails, FileMd5Library, FileServerLinkOptions, FileServerResponse, GetFileDetailsParams, OfficeDocumentViewType, UploadFileParams } from './file-server.types';

const FILE_HASH_CHUNK_SIZE = 2 * 1024 * 1024;

/**
 * Computes the legacy MD5 duplicate-check digest without calling an AngularJS service.
 * Reuses the application's existing SparkMD5 library and reads bounded 2 MiB chunks.
 * Empty files produce the MD5 of empty content; missing library/read failures reject.
 */
export async function hashFile(file: File): Promise<string> {
    const library = (globalThis as typeof globalThis & { SparkMD5?: FileMd5Library }).SparkMD5;
    if (!library) throw Error('File hashing requires SparkMD5');
    const hasher = new library.ArrayBuffer();
    try {
        for (let offset = 0; offset < file.size; offset += FILE_HASH_CHUNK_SIZE) {
            hasher.append(await file.slice(offset, offset + FILE_HASH_CHUNK_SIZE).arrayBuffer());
        }
        return hasher.end();
    } finally {
        hasher.destroy?.();
    }
}

/** Encodes a UTF-8 stored filename using the legacy URL-safe Base64 download protocol. */
export function encodeDownloadFileName(fileName: string): string {
    const binary = Array.from(new TextEncoder().encode(fileName), (byte) => String.fromCharCode(byte)).join('');
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Normalizes a file-server root URL for endpoint construction.
 * @throws Error when no URL is provided.
 */
export function normalizeFileServerUrl(fileServerUrl: string): string {
    if (!fileServerUrl) throw Error('Invalid file server URL');
    return fileServerUrl.replace(/\/+$/, '');
}

/** Builds the legacy multipart upload payload using the endpoint's field names and defaults. */
export function createFileUploadFormData(params: UploadFileParams): FormData {
    const fileUpload = new FormData();
    fileUpload.append('fileUploadObj[replace]', String(params.replace ?? false));
    fileUpload.append('fileUploadObj[maxWidth]', String(params.maxWidth ?? 800));
    fileUpload.append('fileUploadObj[maxHeight]', String(params.maxHeight ?? 400));
    fileUpload.append('fileUploadObj[fileNameTemplate]', params.fileNameSyntax?.filenametemplate ?? '');
    fileUpload.append('fileUploadObj[localFileNameTemplate]', params.fileNameSyntax?.localfilenametemplate ?? '');
    fileUpload.append('fileUploadObj[position]', '');
    fileUpload.append('file', params.file, params.file.name);
    return fileUpload;
}

/** Applies the legacy defaults for the file-details POST body without mutating caller parameters. */
export function createFileDetailsRequest(params: GetFileDetailsParams) {
    return {
        fileItemKeyno: params.fileItemKeyno ?? 0,
        fileName: params.fileName ?? 0,
        maxWidth: params.maxWidth ?? 0,
        maxHeight: params.maxHeight ?? 0,
        wait: params.wait ?? false,
    };
}

/** Copies a file-details result, rewrites its download links and attaches caller correlation. */
export function prepareFileDetails(response: FileDetails | FileServerResponse<FileDetails>, options: FileServerLinkOptions, instanceId?: string): FileDetails {
    const file = { ...unwrapResponse(response) };
    if (file.imageLink) file.imageLink = toFileServerUrl(file.imageLink, options);
    if (file.thumbnailLink) file.thumbnailLink = toFileServerUrl(file.thumbnailLink, options);
    if (instanceId !== undefined) file.instanceId = instanceId;
    return file;
}

/**
 * Rewrites internal server links through the configured proxy and appends the session ID.
 * Preserves encoded query values and fragments; data, blob, and unmatched relative URLs pass through.
 */
export function toFileServerUrl(original: string, options: FileServerLinkOptions): string {
    if (/^(?:blob:|data:)/i.test(original)) return original;

    const proxyId = options.proxyId;
    const proxyIndex = proxyId ? original.indexOf(proxyId) : -1;
    let relativeUrl: string;

    if (proxyIndex >= 0) {
        relativeUrl = original.slice(proxyIndex + proxyId.length);
    } else if (/^https?:\/\//i.test(original)) {
        const url = new URL(original);
        relativeUrl = `${url.pathname}${url.search}${url.hash}`;
    } else {
        return original;
    }

    const [pathAndQuery, fragment] = relativeUrl.split('#', 2);
    const delimiter = pathAndQuery.includes('?') ? '&' : '?';
    return `${normalizeFileServerUrl(options.fileServerUrl)}/${pathAndQuery.replace(/^\/+/, '')}${delimiter}csi=${encodeURIComponent(options.clientSessionId)}${fragment !== undefined ? `#${fragment}` : ''}`;
}

/**
 * Builds an Office host-page URL with encoded document ID, authorization, session and message origin.
 * The caller supplies the origin so this function does not depend on browser or service state.
 */
export function buildOfficeDocumentUrl(fileServerUrl: string, documentId: string, viewType: OfficeDocumentViewType, accessToken: string, clientSessionId: string, postMessageOrigin: string): string {
    const query = new URLSearchParams({
        access_token: accessToken,
        csi: clientSessionId,
        postMessageOrigin,
    });
    return `${normalizeFileServerUrl(fileServerUrl)}/officeHostPage/${viewType}/${encodeURIComponent(documentId)}?${query.toString()}`;
}

/**
 * Returns the payload from a legacy response envelope, or the response itself for a direct result.
 * @throws Error containing the server message when a supported error flag is set.
 */
export function unwrapResponse<T>(response: T | FileServerResponse<T>, failureMessage = 'File server request failed'): T {
    if (!response || typeof response !== 'object') return response;

    const fileServerResponse = response as FileServerResponse<T>;
    const isError = fileServerResponse.IsError ?? fileServerResponse.isError ?? fileServerResponse.iserror ?? false;
    if (isError) throw Error(fileServerResponse.Message || fileServerResponse.message || failureMessage);

    const data = fileServerResponse.Result ?? fileServerResponse.result ?? fileServerResponse.Data ?? fileServerResponse.data;

    return data === undefined ? (response as T) : data;
}

/**
 * Supplies a meaningful MIME type using blob metadata, HTTP headers, filename and image signatures.
 * Returns the original blob when no type is inferred or its existing type is already correct.
 */
export async function ensureContentType(blob: Blob, responseContentType: string | null, contentDisposition: string | null, url: string): Promise<Blob> {
    const contentType = getUsableContentType(blob.type) || getUsableContentType(responseContentType) || getContentTypeFromName(`${contentDisposition ?? ''} ${url}`) || (await detectImageContentType(blob));

    return contentType && contentType !== blob.type ? blob.slice(0, blob.size, contentType) : blob;
}

function getUsableContentType(contentType: string | null): string {
    const normalized = contentType?.split(';')[0].trim().toLowerCase() ?? '';
    return normalized && normalized !== 'application/octet-stream' ? normalized : '';
}

function getContentTypeFromName(value: string): string {
    let decodedValue = value.toLowerCase();

    try {
        decodedValue = decodeURIComponent(decodedValue);
    } catch {
        // The original value can still contain a usable filename extension.
    }

    for (const match of decodedValue.matchAll(/\.([a-z0-9]+)(?=$|[?&#"';\s])/gi)) {
        const contentType = IMAGE_CONTENT_TYPES[match[1]];
        if (contentType) return contentType;
    }
    return '';
}

async function detectImageContentType(blob: Blob): Promise<string> {
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());

    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
    if (bytesToText(bytes, 0, 3) === 'GIF') return 'image/gif';
    if (bytesToText(bytes, 0, 4) === 'RIFF' && bytesToText(bytes, 8, 12) === 'WEBP') return 'image/webp';
    if (bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp';
    if ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00) || (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a)) return 'image/tiff';

    if (bytesToText(bytes, 4, 8) === 'ftyp') {
        const brand = bytesToText(bytes, 8, 12);
        if (['avif', 'avis'].includes(brand)) return 'image/avif';
        if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand)) return 'image/heic';
    }

    const text = await blob.slice(0, 256).text();
    return /<(?:\?xml[^>]*>\s*)?svg(?:\s|>)/i.test(text) ? 'image/svg+xml' : '';
}

function bytesToText(bytes: Uint8Array, start: number, end: number): string {
    return String.fromCharCode(...bytes.slice(start, end));
}

/** MIME types supported by the filename fallback for image downloads. */
const IMAGE_CONTENT_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    svg: 'image/svg+xml',
    heic: 'image/heic',
    heif: 'image/heic',
    avif: 'image/avif',
};
